import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import { HelmetProvider } from "react-helmet-async";

import "./index.css";
import "./i18n";
import "react-toastify/dist/ReactToastify.css";
import "./lib/axios";

import Root from "./routes/root";
import ErrorPage from "./error-page";
import Home from "./routes/home";
import Store from "./routes/store";
import StoreRegister from "./routes/store-register";
import Category from "./routes/category";
import Product from "./routes/product";
import Shop from "./routes/shop";
import Account from "./routes/account";
import EditAccount from "./components/account/edit-account";
import ChangePassword from "./components/account/change-password";
import Favourite from "./components/account/favourite";
import ChangeLanguage from "./components/account/change-language";
import Cart from "./routes/cart";
import RetiredMilitary from "./components/account/retired-military";
import SpecialCustomer from "./components/account/special-customer";
import ShareApp from "./components/account/share-app";
import MyPoints from "./components/account/my-points";
import Orders from "./components/account/orders";
import TermsConditions from "./routes/terms-conditions";
import Offers from "./routes/offers";
import RegisterStore from "./routes/register-store";
import Addresses from "./components/account/addresses";
import AddNewAddress from "./components/account/add-new-address";
import Search from "./routes/search";
import CardContent from "./components/checkout/checkout-gifts/card-content";
import CheckoutSingle from "./routes/checkout-single";
import CheckoutMulti from "./routes/checkout-multi";
import OrderConfirmation from "./routes/order-confirmation";
import CheckoutGifts from "./routes/checkout-gifts";
import CheckoutSacrifices from "./routes/checkout-sacrifices";
import CheckoutDonations from "./routes/checkout-donations";
import Notifications from "./routes/Notifications";
import Wallet from "./routes/wallet";
import ComingSoon from "./components/comingsoon/comingsoon";
import HeaderLogo from "./assets/header-logo";
import CheckoutGiftsPayment from "./routes/checkout-gifts-payment";
import StoreArticle from "./routes/store-articles";
import Faqs from "./routes/faqs";
import Articles from "./routes/articles";
import { setCountry, setCurrency, isSaudiSubdomain } from "./lib/middleware";
// import Cargo from "./routes/cargo";

// --- Detect subdomain ---
const host = window.location.hostname;
const isSaudi = isSaudiSubdomain(host);

// --- Auto-set country if on Saudi subdomain ---
if (isSaudi) {
    setCountry("ksa"); // adjust to your internal code ("sa", "saudi-arabia", etc.)
    setCurrency("SAR");
}
const router = createBrowserRouter([
    {
        path: "/",
        element: <Root />,
        errorElement: <ErrorPage />,
        children: [
            // { path: "/", element: <ComingSoon /> },
            { path: "/", element: <Home /> },
            { path: "home", element: <Home /> },
            // { path: "store", element: <Store /> },
            { path: "notifications", element: <Notifications /> },
            { path: "search", element: <Search /> },
            { path: "category/:categoryId", element: <Category /> },
            { path: "product/:productId", element: <Product /> },
            { path: "shop/:shopId", element: <Shop /> },
            { path: "cart", element: <Cart /> },
            { path: "change-password", element: <ChangePassword /> },
            {
                path: "account",
                element: <Account />,
                children: [
                    { path: "edit-profile", element: <EditAccount /> },
                    { path: "", element: <EditAccount /> },
                    { path: "favourite", element: <Favourite /> },
                    { path: "change-language", element: <ChangeLanguage /> },
                    { path: "retired-military", element: <RetiredMilitary /> },
                    { path: "share-app", element: <ShareApp /> },
                    { path: "orders", element: <Orders /> },
                    { path: "my-points", element: <MyPoints /> },
                    { path: "special-customer", element: <SpecialCustomer /> },
                    { path: "addresses", element: <Addresses /> },
                    { path: "add-new-address", element: <AddNewAddress /> },
                ],
            },
            { path: "offers", element: <Offers /> },
            { path: "register-store", element: <RegisterStore /> },
            { path: "terms-conditions", element: <TermsConditions /> },
            { path: "faqs", element: <Faqs /> },
            { path: "articles", element: <Articles /> },
            { path: "add-new-address", element: <AddNewAddress /> },
            {
                path: "checkout/gifts/:cart_id",
                element: <CheckoutGifts />,
            },
            {
                path: "checkout/gifts/payment",
                element: <CheckoutGiftsPayment />,
            },
            {
                path: "checkout/gifts/card-content/:cart_id",
                element: <CardContent />,
            },
            {
                path: "checkout/sacrifices",
                element: <CheckoutSacrifices />,
            },
            {
                path: "checkout/donations",
                element: <CheckoutDonations />,
            },
            {
                path: "checkout/single",
                element: <CheckoutSingle />,
            },
            {
                path: "checkout/multi",
                element: <CheckoutMulti />,
            },
            {
                path: "order-confirmation",
                element: <OrderConfirmation />,
            },
            {
                path: "wallet",
                element: <Wallet />,
            },

            { path: "*", element: <ErrorPage /> },
        ],
    },
    {
        path: "/store",
        element: <Store />,
    },
    {
        path: "/store/register",
        element: <StoreRegister />,
    },
    {
        path: "/store/articles",
        element: <StoreArticle />,
    },
    // {
    //     path: "/cargo",
    //     element: <Cargo />,
    // },
]);

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
);

root.render(
    <HelmetProvider>
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            {/* <ComingSoon/> */}
            <ToastContainer
                position="bottom-right"
                className="relative z-[99999] !w-auto text-nowrap"
            />
        </QueryClientProvider>
    </HelmetProvider>,
);
